import { type RefObject, useEffect, useRef, useState } from 'react';

/**
 * Reports whether the referenced element's text is horizontally truncated — i.e. its
 * `scrollWidth` exceeds its `clientWidth`, which is what happens when `truncate` /
 * `text-overflow: ellipsis` clamps a single-line label against a `max-width`.
 *
 * Attach the returned `ref` to the truncating text element. `isTruncated` is evaluated on
 * mount, whenever `dependency` changes (pass the label so a text swap re-measures), and
 * whenever the element resizes.
 */
export function useIsTruncated<T extends HTMLElement = HTMLElement>(
  dependency?: unknown
): { ref: RefObject<T>; isTruncated: boolean } {
  const ref = useRef<T>(null);
  const [isTruncated, setIsTruncated] = useState(false);

  useEffect(() => {
    const element = ref.current;
    if (!element) return undefined;

    const measure = () => setIsTruncated(element.scrollWidth > element.clientWidth);
    measure();

    if (typeof ResizeObserver === 'undefined') return undefined;
    const observer = new ResizeObserver(measure);
    observer.observe(element);
    return () => observer.disconnect();
    // `dependency` lets a content change (e.g. a new label) re-trigger measurement, since a
    // text swap that doesn't resize the (max-width-capped) box won't fire the ResizeObserver.
  }, [dependency]);

  return { ref, isTruncated };
}
